import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { KPICard } from "../components/KPICard";
import WhatsNewBanner from "../components/WhatsNewBanner";
import { formatINR } from "../lib/brand";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
  Cell,
} from "recharts";
import { Link } from "react-router-dom";
import { Upload, FileBarChart2, TrendingUp, AlertTriangle, ArrowRight, Swords } from "lucide-react";

const PLATFORM_BAR_COLORS = {
  Myntra: "#F43397",
  Nykaa: "#E5097F",
  Blinkit: "#F8CB46",
  "Swiggy Instamart": "#FC8019",
};

function GlassTooltip({ active, payload, label, prefixMap = {} }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0D0D0D]/95 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 text-xs text-white shadow-[0_15px_40px_-10px_rgba(79,70,229,0.4)]">
      <div className="font-bold mb-1 text-white/90">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-white/60">{p.name}:</span>
          <span className="font-mono-data font-bold">
            {prefixMap[p.dataKey] ? prefixMap[p.dataKey](p.value) : p.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function SellerDashboard() {
  const [data, setData] = useState(null);
  const [risk, setRisk] = useState(null);
  const [trend, setTrend] = useState([]);
  const [competitors, setCompetitors] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/seller/dashboard").then((r) => setData(r.data)).catch(() => {}),
      api.get("/seller/analytics/revenue-at-risk").then((r) => setRisk(r.data)).catch(() => {}),
      api.get("/seller/analytics/trend").then((r) => setTrend(r.data)).catch(() => {}),
      api.get("/seller/competitors/latest").then((r) => setCompetitors(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <AppShell role="seller">
        <div className="text-white/60">Loading dashboard…</div>
      </AppShell>
    );

  const empty = !data?.recent_reports?.length;
  const kpis = data?.kpis || {};

  return (
    <AppShell role="seller">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Dashboard</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">
            Hello, {data?.user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-white/60 text-sm mt-1">Aggregated across all your uploaded reports.</p>
        </div>
        <Link to="/seller/upload" className="btn-primary text-sm" data-testid="dashboard-upload-cta">
          <Upload size={14} className="inline mr-1.5" /> Upload New Data
        </Link>
      </div>

      <WhatsNewBanner />

      {empty && (
        <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-8 text-white mb-8 kpi-shadow" data-testid="empty-state">
          <div className="font-display text-2xl font-black tracking-tighter">Zero reports yet.</div>
          <p className="text-slate-400 mt-2 text-sm">Upload a CSV/XLSX from Myntra, Nykaa, Blinkit or Swiggy Instamart and we'll turn it into an AI Growth Report.</p>
          <Link to="/seller/upload" className="btn-primary inline-block mt-5 text-sm">
            Upload Your First File
          </Link>
        </div>
      )}

      {/* Revenue-at-Risk alert */}
      {risk?.enabled && risk.total_at_risk > 0 && (
        <div className="mb-6 rounded-xl overflow-hidden flex flex-col md:flex-row" data-testid="revenue-at-risk">
          <div className="bg-[#4F46E5] text-white p-6 md:w-1/3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} />
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold">Revenue at Risk</div>
            </div>
            <div className="font-mono-data text-4xl font-medium">{formatINR(risk.total_at_risk)}</div>
            <div className="text-xs text-white/80 mt-2">{risk.platform} · {risk.cancelled_orders + risk.returned_orders + (risk.rto_orders || 0)} orders lost</div>
          </div>
          <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] text-white flex-1 p-6">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Cancelled sales</div>
                <div className="font-mono-data text-xl text-[#4F46E5]" data-testid="risk-cancelled-sales">{formatINR(risk.cancelled_sales)}</div>
                <div className="text-[10px] text-slate-500">{risk.cancelled_orders} orders</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Returned sales</div>
                <div className="font-mono-data text-xl" data-testid="risk-returned-sales">{formatINR(risk.returned_sales)}</div>
                <div className="text-[10px] text-slate-500">{risk.returned_orders} orders</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">RTO sales</div>
                <div className="font-mono-data text-xl text-amber-400" data-testid="risk-rto-sales">{formatINR(risk.rto_sales || 0)}</div>
                <div className="text-[10px] text-slate-500">{risk.rto_orders || 0} orders</div>
              </div>
            </div>
            <div className="text-xs text-slate-400 mb-3">
              Top high-cancel SKUs to fix:{" "}
              <span className="font-bold">
                {risk.avoid_skus.slice(0, 3).map((s) => s.sku).join(", ") || "—"}
              </span>
            </div>
            <Link to="/seller/insights" className="text-xs font-bold text-[#4F46E5] inline-flex items-center gap-1" data-testid="risk-view-details">
              View full AVOID list <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard label="Total Sales" value={formatINR(kpis.total_sales ?? kpis.total_revenue)} accent testId="kpi-sales" />
        <KPICard label="Total Orders" value={(kpis.total_orders ?? 0).toLocaleString()} hint={`${(kpis.total_uploads || 0)} uploads`} testId="kpi-orders" />
        <KPICard label="Avg Order Value" value={formatINR(kpis.avg_order_value)} hint="sales ÷ orders" testId="kpi-aov" />
        <KPICard
          label="Delivery Rate"
          value={`${data?.order_status?.rates?.delivered_pct ?? 0}%`}
          hint={`${kpis.total_reports || 0} reports · ${formatINR(data?.order_status?.delivered_sales)} delivered`}
          testId="kpi-delivery-rate"
        />
      </div>

      {/* Order Status Breakdown (Myntra-style) */}
      {data?.order_status && data.order_status.gross_orders > 0 && (
        <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white mb-8" data-testid="order-status-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Order Status (latest report)</div>
            <div className="text-[10px] font-mono-data text-slate-500">gross {data.order_status.gross_orders}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-4 rounded-lg bg-green-50 border border-green-100">
              <div className="text-[10px] uppercase tracking-widest text-green-700 font-bold">Delivered</div>
              <div className="font-mono-data text-2xl text-green-700">{data.order_status.delivered_orders}</div>
              <div className="text-[11px] text-slate-400 mt-1">{data.order_status.rates?.delivered_pct ?? 0}% · {formatINR(data.order_status.delivered_sales)}</div>
            </div>
            <div className="p-4 rounded-lg bg-orange-50 border border-orange-100">
              <div className="text-[10px] uppercase tracking-widest text-orange-700 font-bold">Cancelled</div>
              <div className="font-mono-data text-2xl text-orange-700" data-testid="status-cancelled-count">{data.order_status.cancelled_orders}</div>
              <div className="text-[11px] text-slate-400 mt-1">{data.order_status.rates?.cancelled_pct ?? 0}% · {formatINR(data.order_status.cancelled_sales)}</div>
            </div>
            <div className="p-4 rounded-lg bg-red-50 border border-red-100">
              <div className="text-[10px] uppercase tracking-widest text-[#4F46E5] font-bold">Returned</div>
              <div className="font-mono-data text-2xl text-[#4F46E5]" data-testid="status-returned-count">{data.order_status.returned_orders}</div>
              <div className="text-[11px] text-slate-400 mt-1">{data.order_status.rates?.returned_pct ?? 0}% · {formatINR(data.order_status.returned_sales)}</div>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">RTO</div>
              <div className="font-mono-data text-2xl text-amber-700" data-testid="status-rto-count">{data.order_status.rto_orders ?? 0}</div>
              <div className="text-[11px] text-slate-400 mt-1">{data.order_status.rates?.rto_pct ?? 0}% · {formatINR(data.order_status.rto_sales || 0)}</div>
            </div>
            <div className="p-4 rounded-lg bg-white/[0.02] border border-zinc-100">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">In Progress</div>
              <div className="font-mono-data text-2xl text-slate-300" data-testid="status-inprogress-count">{data.order_status.in_progress_orders}</div>
              <div className="text-[11px] text-slate-400 mt-1">{data.order_status.rates?.in_progress_pct ?? 0}% · {formatINR(data.order_status.in_progress_sales || 0)} · SH/PK/WP</div>
            </div>
          </div>
        </div>
      )}

      {/* Historical trend area chart */}
      {trend.length > 1 && (
        <div
          className="relative bg-gradient-to-br from-white via-white to-[#FFF5F5] rounded-2xl p-6 text-white mb-8 overflow-hidden shadow-[0_25px_60px_-30px_rgba(79,70,229,0.35)]"
          data-testid="historical-trend"
        >
          <div className="absolute -right-24 -top-24 w-64 h-64 rounded-full bg-[#4F46E5]/10 blur-3xl pointer-events-none" />
          <div className="relative flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">
                Time-series
              </div>
              <div className="font-display text-xl font-black tracking-tight mt-1">
                Sales vs Orders
              </div>
            </div>
            <TrendingUp size={18} className="text-[#4F46E5]" />
          </div>
          <div className="relative h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={trend.map((t) => ({ ...t, label: new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) }))}
                margin={{ left: -10, top: 10, right: 10 }}
              >
                <defs>
                  <linearGradient id="gradSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#09090B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#09090B" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" fontSize={11} stroke="#94a3b8" tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" fontSize={11} stroke="#94a3b8" tickLine={false} axisLine={false} tickFormatter={formatINR} />
                <YAxis yAxisId="right" orientation="right" fontSize={11} stroke="#94a3b8" tickLine={false} axisLine={false} />
                <Tooltip
                  content={
                    <GlassTooltip
                      prefixMap={{ sales: (v) => formatINR(v), orders: (v) => v.toLocaleString() }}
                    />
                  }
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="sales"
                  stroke="#4F46E5"
                  strokeWidth={2.5}
                  fill="url(#gradSales)"
                  name="Sales"
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", fill: "#4F46E5" }}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="orders"
                  stroke="#09090B"
                  strokeWidth={2.5}
                  fill="url(#gradOrders)"
                  name="Orders"
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", fill: "#09090B" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div
          className="relative rounded-2xl p-6 lg:col-span-2 overflow-hidden"
          style={{
            background: "#FFFFFF",
            border: "1px solid #E9E9ED",
            boxShadow: "0 8px 22px -16px rgba(255,63,108,0.15)",
            color: "#0B0C12",
          }}
          data-testid="chart-sales-platform"
        >
          <div className="relative flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#FF3F6C" }}>
                Platform breakdown
              </div>
              <div className="font-display text-xl font-black tracking-tight mt-1" style={{ color: "#0B0C12" }}>
                Sales by Platform
              </div>
            </div>
            <TrendingUp size={18} style={{ color: "#FF3F6C" }} />
          </div>
          <div className="relative h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data?.sales_by_platform || data?.revenue_by_platform || []}
                margin={{ left: -10, top: 10, right: 10 }}
                barCategoryGap="30%"
              >
                <defs>
                  {Object.entries(PLATFORM_BAR_COLORS).map(([pid, c]) => (
                    <linearGradient key={pid} id={`grad-${pid.replace(/\s+/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={1} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.35} />
                    </linearGradient>
                  ))}
                  <linearGradient id="grad-default" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4F46E5" stopOpacity={1} />
                    <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.35} />
                  </linearGradient>
                  <filter id="barGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="4" result="glow" />
                    <feMerge>
                      <feMergeNode in="glow" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="platform"
                  fontSize={11}
                  stroke="rgba(255,255,255,0.5)"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  fontSize={11}
                  stroke="rgba(255,255,255,0.5)"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatINR}
                />
                <Tooltip
                  cursor={{ fill: "rgba(79,70,229,0.08)" }}
                  content={
                    <GlassTooltip
                      prefixMap={{
                        sales: (v) => formatINR(v),
                        revenue: (v) => formatINR(v),
                      }}
                    />
                  }
                />
                <Bar
                  dataKey={data?.sales_by_platform ? "sales" : "revenue"}
                  radius={[10, 10, 0, 0]}
                  filter="url(#barGlow)"
                >
                  {(data?.sales_by_platform || data?.revenue_by_platform || []).map((row, idx) => {
                    const id = `grad-${(row.platform || "").replace(/\s+/g, "")}`;
                    const hasColor = PLATFORM_BAR_COLORS[row.platform];
                    return <Cell key={idx} fill={hasColor ? `url(#${id})` : "url(#grad-default)"} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="top-skus">
          <div className="text-sm font-bold uppercase tracking-[0.2em] mb-4">Top SKUs (latest)</div>
          {(!data?.top_skus || data.top_skus.length === 0) && (
            <div className="text-sm text-slate-500">Upload data to see top SKUs.</div>
          )}
          <div className="space-y-2">
            {(data?.top_skus || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-md bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <span className="font-mono-data text-xs text-[#4F46E5] w-6">#{i + 1}</span>
                  <div>
                    <span className="text-xs font-bold truncate block max-w-[140px]">{s.sku}</span>
                    <span className="text-[10px] text-slate-500 font-mono-data">{s.orders ?? 0} orders</span>
                  </div>
                </div>
                <span className="text-xs font-mono-data">{formatINR(s.sales ?? s.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="top-cities">
          <div className="text-sm font-bold uppercase tracking-[0.2em] mb-4">Top Cities by Sales</div>
          {(!data?.top_cities || data.top_cities.length === 0) && (
            <div className="text-sm text-slate-500">No city data yet.</div>
          )}
          <div className="space-y-2">
            {(data?.top_cities || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-md bg-white/[0.02]">
                <span className="text-xs font-bold">{c.city}</span>
                <span className="text-xs font-mono-data text-[#4F46E5]">{formatINR(c.sales ?? c.revenue)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="recent-reports">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Recent Reports</div>
            <Link to="/seller/reports" className="text-xs text-[#4F46E5] font-bold">All →</Link>
          </div>
          {(!data?.recent_reports || data.recent_reports.length === 0) && (
            <div className="text-sm text-slate-500">No reports yet.</div>
          )}
          <div className="space-y-2">
            {(data?.recent_reports || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-md bg-white/[0.02]">
                <div>
                  <div className="text-xs font-bold">{r.platform}</div>
                  <div className="text-[11px] text-slate-500 font-mono-data">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <FileBarChart2 size={16} className="text-[#4F46E5]" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Competitor Watch teaser */}
      {competitors?.enabled && competitors.comparisons?.length > 0 && (() => {
        const firstGroup = competitors.comparisons[0];
        const comps = (firstGroup?.competitors || []).slice(0, 3);
        if (comps.length === 0) return null;
        return (
          <div className="mt-8" data-testid="dashboard-competitor-teaser">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold inline-flex items-center gap-1.5">
                  <Swords size={12} /> Competitor Watch
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1">
                  Your <span className="text-[#4F46E5]">{firstGroup.your_article.article_type}</span> vs {competitors.platform}.
                </h2>
                <div className="text-xs text-white/50 mt-1">
                  Your avg price{" "}
                  <span className="font-mono-data text-white">
                    {firstGroup.your_article.avg_price
                      ? formatINR(firstGroup.your_article.avg_price)
                      : "—"}
                  </span>
                  {" · "}
                  {(firstGroup.your_article.orders || 0).toLocaleString()} orders
                </div>
              </div>
              <Link
                to="/seller/competitors"
                className="text-xs font-bold text-[#4F46E5] inline-flex items-center gap-1"
                data-testid="dashboard-competitor-view-all"
              >
                Compare all articles <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {comps.map((c, i) => {
                const d = c.delta || {};
                const deltaColor =
                  d.cheaper === true
                    ? "bg-emerald-50 text-emerald-700"
                    : d.cheaper === false
                    ? "bg-rose-50 text-rose-700"
                    : "bg-white/[0.04] text-slate-400";
                return (
                  <a
                    key={i}
                    href={c.link}
                    target="_blank"
                    rel="noreferrer"
                    className="group bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl overflow-hidden text-white flex hover:ring-2 hover:ring-[#4F46E5]/60 transition-all"
                    data-testid={`dashboard-competitor-${i}`}
                  >
                    <div className="w-24 h-28 shrink-0 overflow-hidden bg-white/[0.04]">
                      <img
                        src={c.image_url}
                        alt={c.product_name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://images.unsplash.com/photo-1607082349566-187342175e2f?w=600&q=80";
                        }}
                      />
                    </div>
                    <div className="flex-1 p-3 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                            {c.brand}
                          </div>
                          {d.label && d.label !== "—" && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${deltaColor}`}>
                              {d.label}
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-bold leading-tight line-clamp-2 mt-0.5">
                          {c.product_name}
                        </div>
                      </div>
                      <div className="font-mono-data text-lg font-black">
                        {formatINR(c.price_inr)}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        );
      })()}
    </AppShell>
  );
}
